// Model Schema Definition System
// Pre-defined schemas for each model type with strict validation

import { parseUtcDateTime } from '../utils/datetimeField';

export type FieldType =
  | 'string'
  | 'int'
  | 'float'
  | 'boolean'
  | 'enum' // Enumeration with predefined value/label pairs and configurable value type
  | 'datetime'
  | 'dict'
  | 'forecast' // Forecast data selection (parameter, source, location)
  | 'dataSource' // Data source selection (source, timeType, dataType)
  | 'file_reference'
  | '2d_array'
  | 'asset_boundary' // Select existing asset OR upload KMZ/KML/shapefile
  | 'asset_location' // Point location: link an asset (resolved at run time) OR enter lat/lon manually
  | 'xy_curve' // Relationship curve built as x/y point pairs with a graph preview
  | 'dataset' // HISTORICAL models: a curated historical dataset and its columns (docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, stage 3)
  | 'model_output'; // Another model's output param and its columns (docs/architecture/MODEL_OUTPUT_INPUTS.md)

// Enum option with value and display label
export interface EnumOption {
  value: string | number;
  label: string;
}

// Value type for enum fields
export type EnumValueType = 'string' | 'int' | 'float';

export interface FieldDefinition {
  type: FieldType;
  required: boolean;
  label: string;
  description?: string;
  placeholder?: string;
  section?: string; // Group fields into sections (e.g., "outputs", "inputs", "configuration")
  order?: number; // Sort position within its section (lower = earlier). Falls back to insertion order when absent.
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    // For enum fields:
    enumValueType?: EnumValueType; // 'string', 'int', or 'float'
    enumOptions?: EnumOption[]; // Array of { value, label } pairs
    multiSelect?: boolean; // Allow selecting multiple options (value stored as array)
    columns?: string[]; // For 2d_array
    fileTypes?: string[]; // For file_reference and asset_boundary
    // For asset_boundary:
    allowAssetReference?: boolean; // Allow selecting existing assets
    allowFileUpload?: boolean; // Allow uploading files
    multipleAssets?: boolean; // Allow selecting multiple assets
    requiredAssetTypes?: string[]; // Filter assets by type/category (future use)
    // For asset_location (also uses allowAssetReference above):
    allowManualInput?: boolean; // Allow manual latitude/longitude entry
    // For numeric fields (int, float) - standardized units:
    measurementType?: string; // e.g., 'distance_1d', 'flow_rate', 'power'
    allowedUnits?: string[]; // Units user can select, e.g., ['m', 'km', 'ft']
    // For datetime fields:
    minDateTime?: string; // Min date/time in ISO 8601 format
    maxDateTime?: string; // Max date/time in ISO 8601 format
    // For dict (JSON object) fields:
    requiredKeys?: string[]; // Required keys in the JSON object
    // For a dict that holds per-column quality rules: the key of the dataset field whose ticked columns it describes
    // (stage 4 of docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md; the wizard shows a form instead of JSON)
    rulesFor?: string;
    // For xy_curve fields — axis display config (table headers + graph labels):
    xAxisLabel?: string; // e.g., 'Wind Speed'
    xAxisUnit?: string; // e.g., 'm/s'
    yAxisLabel?: string; // e.g., 'Power'
    yAxisUnit?: string; // e.g., 'MW'
    // For forecast fields:
    allowedParameters?: string[]; // e.g., ['precipitation', 'temperature', 'wind10m']
    allowedSources?: string[]; // e.g., ['HRRR', 'GEFS']
    allowLocationInput?: boolean; // Allow manual lat/lon entry
    allowAssetLocationSelection?: boolean; // Allow selecting from existing assets
    allowProcessedModels?: boolean; // Allow selecting processed models (future)
    // For dataSource fields - hierarchical data source configuration:
    dataSourceConfig?: {
      // Each data source can have multiple time types
      [sourceName: string]: {
        label: string;
        timeTypes: {
          // Each time type can have multiple data types
          [timeTypeName: string]: {
            label: string;
            dataTypes: string[]; // e.g., ['flowRate', 'energy', 'price']
          };
        };
      };
    };
    // For dataset fields (stage 3): pick one column or several; only curated datasets can be picked
    datasetConfig?: {
      columns?: 'single' | 'multi';
      requireCurated?: boolean;
    };
    // For model_output fields: pick one column or several; the window a FORECAST model reads on each run (ISO 8601
    // duration, default P7D) and how many issues of the FILES output it takes (default 1). A HISTORICAL model reads
    // its run's window instead. Only FILES outputs can be linked (a historical model's data is in the dataset it
    // writes, linked with a dataset field); storageModes is no longer read, it only survives on stored templates.
    modelOutputConfig?: {
      columns?: 'single' | 'multi';
      storageModes?: string[];
      lookback?: string;
      latestIssues?: number;
    };
  };
  defaultValue?: any;
  unit?: string; // Display unit for context
  // Retired flag: every output of a HISTORICAL template goes to a dataset (docs/architecture/HISTORICAL_DATA_RUNNERS.md);
  // kept so stored templates still parse, ignored by the wizard
  canWriteDataset?: boolean;
}

export interface ModelSchema {
  id: string;
  name: string;
  category: string;
  description: string;
  version: string;
  executionUrl: string; // Cloud Run or Lambda endpoint URL for model execution
  fields: Record<string, FieldDefinition>;
  isActive: boolean;
}

// Pre-defined model schemas
export const MODEL_SCHEMAS: Record<string, ModelSchema> = {
  'wind-farm-v1': {
    id: 'wind-farm-v1',
    name: 'Wind Farm',
    category: 'Energy',
    description: 'Wind farm modeling with power curves and capacity',
    version: '1.0',
    executionUrl: 'https://wind-farm-model-service-xyz.run.app/execute',
    isActive: true,
    fields: {
      siteName: {
        type: 'string',
        required: true,
        label: 'Site Name',
        description: 'Name of the wind farm site',
        placeholder: 'West Texas Wind Farm',
        section: 'configuration'
      },
      latitude: {
        type: 'float',
        required: true,
        label: 'Latitude',
        description: 'Geographic latitude',
        section: 'configuration',
        validation: {
          min: -90,
          max: 90
        }
      },
      longitude: {
        type: 'float',
        required: true,
        label: 'Longitude', 
        description: 'Geographic longitude',
        section: 'configuration',
        validation: {
          min: -180,
          max: 180
        }
      },
      totalCapacity: {
        type: 'string',
        required: true,
        label: 'Total Installed Capacity',
        description: 'Total power generation capacity',
        placeholder: '100 MW',
        unit: 'MW',
        section: 'inputs'
      },
      powerCurve: {
        type: 'file_reference',
        required: true,
        label: 'Power Curve Data',
        description: 'Wind speed vs power output relationship',
        section: 'inputs',
        validation: {
          fileTypes: ['csv', 'json', 'xlsx']
        }
      },
      turbineCount: {
        type: 'int',
        required: true,
        label: 'Number of Turbines',
        description: 'Total number of wind turbines',
        section: 'inputs',
        validation: {
          min: 1,
          max: 1000
        }
      },
      hubHeight: {
        type: 'string',
        required: false,
        label: 'Hub Height',
        description: 'Height of turbine hubs above ground',
        placeholder: '80 m',
        unit: 'm',
        section: 'inputs'
      },
      turbineType: {
        type: 'enum',
        required: false,
        label: 'Turbine Type',
        description: 'Manufacturer and model of turbines',
        section: 'inputs',
        validation: {
          enumValueType: 'string',
          enumOptions: [
            { value: 'vestas-v90', label: 'Vestas V90' },
            { value: 'ge-2.5mw', label: 'GE 2.5MW' },
            { value: 'siemens-swt-3.6', label: 'Siemens SWT-3.6' },
            { value: 'enercon-e-126', label: 'Enercon E-126' },
            { value: 'custom', label: 'Custom' }
          ]
        }
      },
      commissionDate: {
        type: 'string',
        required: false,
        label: 'Commission Date',
        description: 'When the farm became operational',
        placeholder: '2023-01-15',
        section: 'configuration'
      },
      isOperational: {
        type: 'boolean',
        required: true,
        label: 'Currently Operational',
        description: 'Whether the farm is currently generating power',
        defaultValue: true,
        section: 'configuration'
      },
      outputPowerGeneration: {
        type: 'boolean',
        required: true,
        label: 'Power Generation',
        description: 'Include power generation forecasts in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputWindSpeed: {
        type: 'boolean',
        required: true,
        label: 'Wind Speed Analysis',
        description: 'Include wind speed analysis in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputCapacityFactor: {
        type: 'boolean',
        required: true,
        label: 'Capacity Factor',
        description: 'Include capacity factor calculations in model output',
        section: 'outputs',
        defaultValue: false
      }
    }
  },

  'solar-farm-v1': {
    id: 'solar-farm-v1',
    name: 'Solar Farm',
    category: 'Energy',
    description: 'Solar photovoltaic farm modeling',
    version: '1.0',
    executionUrl: 'https://solar-farm-model-service-abc.run.app/execute',
    isActive: true,
    fields: {
      siteName: {
        type: 'string',
        required: true,
        label: 'Site Name',
        description: 'Name of the solar farm site',
        placeholder: 'Mojave Solar Farm',
        section: 'configuration'
      },
      latitude: {
        type: 'float',
        required: true,
        label: 'Latitude',
        description: 'Geographic latitude',
        section: 'configuration',
        validation: {
          min: -90,
          max: 90
        }
      },
      longitude: {
        type: 'float',
        required: true,
        label: 'Longitude',
        description: 'Geographic longitude',
        section: 'configuration',
        validation: {
          min: -180,
          max: 180
        }
      },
      installedCapacity: {
        type: 'string',
        required: true,
        label: 'Installed Capacity',
        description: 'Peak power generation capacity',
        placeholder: '50 MW',
        unit: 'MW',
        section: 'inputs'
      },
      panelEfficiency: {
        type: 'float',
        required: false,
        label: 'Panel Efficiency',
        description: 'Solar panel efficiency percentage',
        section: 'inputs',
        validation: {
          min: 0.1,
          max: 0.5
        },
        unit: '%'
      },
      trackingType: {
        type: 'enum',
        required: true,
        label: 'Tracking System',
        description: 'Type of solar tracking system',
        section: 'inputs',
        validation: {
          enumValueType: 'string',
          enumOptions: [
            { value: 'fixed-tilt', label: 'Fixed Tilt' },
            { value: 'single-axis', label: 'Single Axis' },
            { value: 'dual-axis', label: 'Dual Axis' },
            { value: 'none', label: 'None' }
          ]
        },
        defaultValue: 'fixed-tilt'
      },
      tiltAngle: {
        type: 'string',
        required: false,
        label: 'Panel Tilt Angle',
        description: 'Angle of panel tilt from horizontal',
        placeholder: '25°',
        unit: 'degrees',
        section: 'inputs'
      },
      inverterData: {
        type: 'file_reference',
        required: false,
        label: 'Inverter Specifications',
        description: 'Inverter efficiency and performance data',
        section: 'inputs',
        validation: {
          fileTypes: ['csv', 'json', 'pdf']
        }
      },
      outputPowerGeneration: {
        type: 'boolean',
        required: true,
        label: 'Power Generation',
        description: 'Include power generation forecasts in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputSolarIrradiance: {
        type: 'boolean',
        required: true,
        label: 'Solar Irradiance',
        description: 'Include solar irradiance analysis in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputEfficiencyAnalysis: {
        type: 'boolean',
        required: true,
        label: 'Efficiency Analysis',
        description: 'Include panel efficiency analysis in model output',
        section: 'outputs',
        defaultValue: false
      }
    }
  },

  'watershed-v1': {
    id: 'watershed-v1',
    name: 'Watershed',
    category: 'Hydrology',
    description: 'Watershed flow and drainage analysis',
    version: '1.0',
    executionUrl: 'https://watershed-model-service-def.run.app/execute',
    isActive: true,
    fields: {
      bounds: {
        type: 'file_reference',
        required: true,
        label: 'Watershed Boundary',
        description: 'Geospatial file defining watershed boundaries',
        section: 'inputs',
        validation: {
          fileTypes: ['shp', 'geojson', 'kml']
        }
      },
      catchmentArea: {
        type: 'string',
        required: true,
        label: 'Catchment Area',
        description: 'Total area of the watershed',
        placeholder: '1500 km²',
        unit: 'km²',
        section: 'configuration'
      },
      averageElevation: {
        type: 'string',
        required: false,
        label: 'Average Elevation',
        description: 'Mean elevation of the watershed',
        placeholder: '850 m',
        unit: 'm',
        section: 'configuration'
      },
      soilType: {
        type: 'enum',
        required: false,
        label: 'Predominant Soil Type',
        description: 'Main soil classification in the watershed',
        section: 'configuration',
        validation: {
          enumValueType: 'string',
          enumOptions: [
            { value: 'clay', label: 'Clay' },
            { value: 'sand', label: 'Sand' },
            { value: 'loam', label: 'Loam' },
            { value: 'rocky', label: 'Rocky' },
            { value: 'mixed', label: 'Mixed' },
            { value: 'unknown', label: 'Unknown' }
          ]
        }
      },
      landUse: {
        type: 'enum',
        required: false,
        label: 'Primary Land Use',
        description: 'Main land use category',
        section: 'configuration',
        validation: {
          enumValueType: 'string',
          enumOptions: [
            { value: 'forest', label: 'Forest' },
            { value: 'agriculture', label: 'Agriculture' },
            { value: 'urban', label: 'Urban' },
            { value: 'grassland', label: 'Grassland' },
            { value: 'mixed', label: 'Mixed' },
            { value: 'other', label: 'Other' }
          ]
        }
      },
      precipitationData: {
        type: 'file_reference',
        required: false,
        label: 'Historical Precipitation',
        description: 'Time series precipitation data',
        section: 'inputs',
        validation: {
          fileTypes: ['csv', 'json', 'netcdf']
        }
      },
      flowGaugeData: {
        type: 'file_reference',
        required: false,
        label: 'Stream Gauge Data',
        description: 'Historical flow measurements',
        section: 'inputs',
        validation: {
          fileTypes: ['csv', 'json']
        }
      },
      outputStreamflow: {
        type: 'boolean',
        required: true,
        label: 'Streamflow',
        description: 'Include streamflow calculations in model output',
        section: 'outputs',
        defaultValue: false
      },
      outputRunoff: {
        type: 'boolean',
        required: true,
        label: 'Surface Runoff',
        description: 'Include surface runoff analysis in model output',
        section: 'outputs',
        defaultValue: false
      },
      outputEvapotranspiration: {
        type: 'boolean',
        required: true,
        label: 'Evapotranspiration',
        description: 'Include evapotranspiration estimates in model output',
        section: 'outputs',
        defaultValue: false
      },
      outputWatershedPrecip: {
        type: 'boolean',
        required: true,
        label: 'Watershed Precipitation',
        description: 'weighted average watershed precipitation',
        section: 'outputs',
        defaultValue: true
      }
    }
  },

  'flood-risk-v1': {
    id: 'flood-risk-v1',
    name: 'Flood Risk Assessment',
    category: 'Hydrology',
    description: 'Flood modeling and risk assessment',
    version: '1.0',
    executionUrl: 'https://flood-risk-model-service-ghi.run.app/execute',
    isActive: true,
    fields: {
      siteName: {
        type: 'string',
        required: true,
        label: 'Site Name',
        description: 'Name of the flood risk study area',
        placeholder: 'Houston Flood Zone Assessment',
        section: 'configuration'
      },
      latitude: {
        type: 'float',
        required: true,
        label: 'Study Area Latitude',
        description: 'Center latitude of study area',
        section: 'configuration',
        validation: {
          min: -90,
          max: 90
        }
      },
      longitude: {
        type: 'float',
        required: true,
        label: 'Study Area Longitude',
        description: 'Center longitude of study area',
        section: 'configuration',
        validation: {
          min: -180,
          max: 180
        }
      },
      elevationModel: {
        type: 'file_reference',
        required: true,
        label: 'Digital Elevation Model',
        description: 'High-resolution terrain data',
        section: 'inputs',
        validation: {
          fileTypes: ['tiff', 'netcdf', 'asc']
        }
      },
      returnPeriods: {
        type: 'string',
        required: true,
        label: 'Return Periods',
        description: 'Flood return periods to analyze (comma-separated)',
        placeholder: '10, 25, 50, 100 years',
        section: 'configuration'
      },
      infrastructureData: {
        type: 'file_reference',
        required: false,
        label: 'Infrastructure Assets',
        description: 'Critical infrastructure locations',
        section: 'inputs',
        validation: {
          fileTypes: ['shp', 'geojson', 'csv']
        }
      },
      populationDensity: {
        type: 'string',
        required: false,
        label: 'Population Density',
        description: 'People per square kilometer',
        placeholder: '1500 people/km²',
        unit: 'people/km²',
        section: 'configuration'
      },
      outputFloodDepth: {
        type: 'boolean',
        required: true,
        label: 'Flood Depth',
        description: 'Include flood depth maps in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputFloodExtent: {
        type: 'boolean',
        required: true,
        label: 'Flood Extent',
        description: 'Include flood extent boundaries in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputRiskAssessment: {
        type: 'boolean',
        required: true,
        label: 'Risk Assessment',
        description: 'Include infrastructure risk analysis in model output',
        section: 'outputs',
        defaultValue: false
      }
    }
  },

  'wind-power-v1': {
    id: 'wind-power-v1',
    name: 'Wind Power',
    category: 'Energy',
    description: 'Wind power generation analysis with turbine specifications',
    version: '1.0',
    executionUrl: 'https://wind-power-model-service-jkl.run.app/execute',
    isActive: true,
    fields: {
      siteName: {
        type: 'string',
        required: false,
        label: 'Site Name',
        description: 'Name of the wind power site',
        placeholder: 'West Texas Wind Power Plant',
        section: 'configuration'
      },
      latitude: {
        type: 'float',
        required: true,
        label: 'Latitude',
        description: 'Geographic latitude of wind site',
        section: 'configuration',
        validation: {
          min: -90,
          max: 90
        }
      },
      longitude: {
        type: 'float',
        required: true,
        label: 'Longitude',
        description: 'Geographic longitude of wind site',
        section: 'configuration',
        validation: {
          min: -180,
          max: 180
        }
      },
      numTurbines: {
        type: 'int',
        required: true,
        label: 'Number of Turbines',
        description: 'Total number of wind turbines at the site',
        section: 'inputs',
        validation: {
          min: 1,
          max: 1000
        }
      },
      installedCapacity: {
        type: 'string',
        required: true,
        label: 'Installed Capacity (MW)',
        description: 'Total installed power capacity',
        placeholder: '150 MW',
        unit: 'MW',
        section: 'inputs'
      },
      powerCurve: {
        type: '2d_array',
        required: true,
        label: 'Power Curve',
        description: 'Array of wind speeds (m/s) and corresponding power output (MW)',
        section: 'inputs',
        validation: {
          columns: ['wind_speed_ms', 'power_output_mw']
        }
      },
      outputPowerGeneration: {
        type: 'boolean',
        required: true,
        label: 'Power Generation',
        description: 'Include power generation forecasts in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputWindSpeed: {
        type: 'boolean',
        required: true,
        label: 'Wind Speed Analysis',
        description: 'Include wind speed analysis in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputTurbinePerformance: {
        type: 'boolean',
        required: true,
        label: 'Turbine Performance',
        description: 'Include individual turbine performance metrics in model output',
        section: 'outputs',
        defaultValue: false
      }
    }
  },

  'watershed-snowpack-v1': {
    id: 'watershed-snowpack-v1',
    name: 'Watershed Snowpack',
    category: 'Hydrology',
    description: 'Snowpack analysis and water equivalent modeling',
    version: '1.0',
    executionUrl: 'https://snowpack-model-service-mno.run.app/execute',
    isActive: true,
    fields: {
      siteName: {
        type: 'string',
        required: false,
        label: 'Site Name',
        description: 'Name or description of the study area',
        placeholder: 'Colorado River Basin Snowpack',
        section: 'configuration'
      },
      latitude: {
        type: 'float',
        required: true,
        label: 'Latitude',
        description: 'Geographic latitude of study area',
        section: 'configuration',
        validation: {
          min: -90,
          max: 90
        }
      },
      longitude: {
        type: 'float',
        required: true,
        label: 'Longitude',
        description: 'Geographic longitude of study area',
        section: 'configuration',
        validation: {
          min: -180,
          max: 180
        }
      },
      bounds: {
        type: 'file_reference',
        required: true,
        label: 'Study Area Boundary',
        description: 'GeoJSON file defining the watershed or study area boundary',
        section: 'inputs',
        validation: {
          fileTypes: ['geojson', 'json', 'kml', 'csv']
        }
      },
      outputSnowDepth: {
        type: 'boolean',
        required: true,
        label: 'Snow Depth',
        description: 'Include snow depth measurements in model output',
        section: 'outputs',
        defaultValue: false
      },
      outputSnowWaterEquivalent: {
        type: 'boolean',
        required: true,
        label: 'Snow Water Equivalent',
        description: 'Include snow water equivalent calculations in model output',
        section: 'outputs',
        defaultValue: true
      },
      outputFractionSnowCovered: {
        type: 'boolean',
        required: true,
        label: 'Fraction Snow Covered',
        description: 'Include fraction of area covered by snow in model output',
        section: 'outputs',
        defaultValue: false
      }
    }
  },

  'custom-v1': {
    id: 'custom-v1',
    name: 'Custom Model',
    category: 'Other',
    description: 'Flexible custom model with user-defined parameters',
    version: '1.0',
    executionUrl: '', // User will need to provide custom execution URL
    isActive: true,
    fields: {
      modelName: {
        type: 'string',
        required: true,
        label: 'Model Name',
        description: 'Name for your custom model',
        placeholder: 'My Custom Model',
        section: 'configuration'
      },
      description: {
        type: 'string',
        required: false,
        label: 'Model Description',
        description: 'Detailed description of your model',
        placeholder: 'Describe what your model does...',
        section: 'configuration'
      },
      inputData: {
        type: 'file_reference',
        required: false,
        label: 'Input Data File',
        description: 'Primary data file for your model',
        section: 'inputs',
        validation: {
          fileTypes: ['csv', 'json', 'xlsx', 'txt', 'netcdf']
        }
      },
      configurationFile: {
        type: 'file_reference',
        required: false,
        label: 'Configuration File',
        description: 'Model configuration or parameters file',
        section: 'inputs',
        validation: {
          fileTypes: ['json', 'yaml', 'txt', 'xml']
        }
      },
      outputResults: {
        type: 'boolean',
        required: true,
        label: 'Model Results',
        description: 'Include primary model results in output',
        section: 'outputs',
        defaultValue: true
      },
      outputDiagnostics: {
        type: 'boolean',
        required: true,
        label: 'Diagnostics',
        description: 'Include model diagnostics and performance metrics in output',
        section: 'outputs',
        defaultValue: false
      }
    }
  }
};

// Utility functions
export const getModelSchema = (schemaId: string): ModelSchema | undefined => {
  return MODEL_SCHEMAS[schemaId];
};

export const getActiveModelSchemas = (): ModelSchema[] => {
  return Object.values(MODEL_SCHEMAS).filter(schema => schema.isActive);
};

export const getModelSchemasByCategory = (category: string): ModelSchema[] => {
  return Object.values(MODEL_SCHEMAS).filter(
    schema => schema.isActive && schema.category === category
  );
};

// Convert ModelSchema to dropdown option format
export interface ModelTypeOption {
  id: string;
  name: string;
  description: string;
  category: string;
}

export const getModelTypeOptions = (): ModelTypeOption[] => {
  return getActiveModelSchemas().map(schema => ({
    id: schema.id,
    name: schema.name,
    description: schema.description,
    category: schema.category
  }));
};

// Mock the fetchModelTypes function for backward compatibility
export const fetchModelTypes = async (): Promise<ModelTypeOption[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(getModelTypeOptions());
    }, 500);
  });
};

export const validateFieldValue = (
  inputValue: any, 
  field: FieldDefinition
): { isValid: boolean; error?: string } => {
  // Unwrap value if it matches our metadata wrapper pattern for primitive types
  let value = inputValue;
  if (
    ['string', 'int', 'float', 'boolean', 'enum', 'datetime', 'xy_curve'].includes(field.type) &&
    inputValue && 
    typeof inputValue === 'object' && 
    'value' in inputValue
  ) {
    value = inputValue.value;
  }

  // Required field validation
  if (
    field.required &&
    (value === null || value === undefined || value === '' ||
      (Array.isArray(value) && value.length === 0))
  ) {
    return { isValid: false, error: `${field.label} is required` };
  }

  // Optional field left blank: valid, skip type-specific validation
  if (
    !field.required &&
    (value === null || value === undefined || value === '' ||
      (Array.isArray(value) && value.length === 0))
  ) {
    return { isValid: true };
  }

  // Type-specific validation
  switch (field.type) {
    case 'int':
      const intValue = parseInt(value);
      if (isNaN(intValue)) {
        return { isValid: false, error: `${field.label} must be a whole number` };
      }
      if (field.validation?.min !== undefined && intValue < field.validation.min) {
        return { isValid: false, error: `${field.label} must be at least ${field.validation.min}` };
      }
      if (field.validation?.max !== undefined && intValue > field.validation.max) {
        return { isValid: false, error: `${field.label} must be at most ${field.validation.max}` };
      }
      break;

    case 'float':
      const floatValue = parseFloat(value);
      if (isNaN(floatValue)) {
        return { isValid: false, error: `${field.label} must be a number` };
      }
      if (field.validation?.min !== undefined && floatValue < field.validation.min) {
        return { isValid: false, error: `${field.label} must be at least ${field.validation.min}` };
      }
      if (field.validation?.max !== undefined && floatValue > field.validation.max) {
        return { isValid: false, error: `${field.label} must be at most ${field.validation.max}` };
      }
      break;


    case 'enum':
      if (field.validation?.enumOptions) {
        const validValues = field.validation.enumOptions.map((opt: any) => opt.value);
        const validLabels = field.validation.enumOptions.map((opt: any) => opt.label).join(', ');
        if (field.validation.multiSelect) {
          // Coerce a legacy scalar (template toggled to multi-select later) into an array
          const values = Array.isArray(value)
            ? value
            : (value === null || value === undefined || value === '' ? [] : [value]);
          const invalid = values.filter((v: any) => !validValues.includes(v));
          if (invalid.length > 0) {
            return { isValid: false, error: `${field.label} selections must be from: ${validLabels}` };
          }
        } else if (!validValues.includes(value)) {
          return { isValid: false, error: `${field.label} must be one of: ${validLabels}` };
        }
      }
      break;

    case 'datetime': {
      const dateValue = parseUtcDateTime(value);
      if (!dateValue) {
        return { isValid: false, error: `${field.label} must be a valid date and time` };
      }
      const minDate = parseUtcDateTime(field.validation?.minDateTime);
      if (minDate && dateValue < minDate) {
        return { isValid: false, error: `${field.label} must be on or after ${field.validation?.minDateTime} (UTC)` };
      }
      const maxDate = parseUtcDateTime(field.validation?.maxDateTime);
      if (maxDate && dateValue > maxDate) {
        return { isValid: false, error: `${field.label} must be on or before ${field.validation?.maxDateTime} (UTC)` };
      }
      break;
    }

    case 'asset_location': {
      if (typeof value !== 'object' || Array.isArray(value)) {
        return { isValid: false, error: `${field.label} must be a manual coordinate or a linked asset` };
      }
      if (value.mode === 'manual') {
        const lat = Number(value.latitude);
        const lon = Number(value.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          return { isValid: false, error: `${field.label} must have numeric latitude and longitude` };
        }
        if (lat < -90 || lat > 90) {
          return { isValid: false, error: `${field.label} latitude must be between -90 and 90` };
        }
        if (lon < -180 || lon > 180) {
          return { isValid: false, error: `${field.label} longitude must be between -180 and 180` };
        }
      } else if (value.mode === 'asset_reference') {
        if (!Array.isArray(value.selectedAssetIds) || value.selectedAssetIds.length === 0) {
          return { isValid: false, error: `${field.label} must have a linked asset` };
        }
      } else {
        return { isValid: false, error: `${field.label} must be a manual coordinate or a linked asset` };
      }
      break;
    }

    case 'dataset': {
      // {type: "dataset", datasetId, columns[]}: the run function links the dataset from this (stage 3)
      if (!value || typeof value !== 'object' || !value.datasetId) {
        return { isValid: false, error: `${field.label}: choose a dataset` };
      }
      if (!Array.isArray(value.columns) || value.columns.length === 0) {
        return { isValid: false, error: `${field.label}: tick at least one column` };
      }
      break;
    }
    case 'xy_curve': {
      // Optional and empty is fine; the required check above already ran
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        break;
      }
      if (!Array.isArray(value)) {
        return { isValid: false, error: `${field.label} must be a list of x/y points` };
      }
      const hasInvalidPoint = value.some(
        (p: any) =>
          !p || typeof p !== 'object' ||
          !Number.isFinite(Number(p.x)) || !Number.isFinite(Number(p.y))
      );
      if (hasInvalidPoint) {
        return { isValid: false, error: `${field.label} points must have numeric x and y values` };
      }
      if (value.length < 2) {
        return { isValid: false, error: `${field.label} needs at least 2 points` };
      }
      break;
    }
  }

  return { isValid: true };
};